import { useMutation, useQueryClient } from 'react-query'
import { message } from 'antd'
import { postJSON } from 'common/services/api'
import { Procedure } from '../types'

interface UseProcedureArchiveReturn {
  archiveProcedure: (procedureId: string) => Promise<Procedure>
  unarchiveProcedure: (procedureId: string) => Promise<Procedure>
  isArchiving: boolean
}

export const useProcedureArchive = (
  residentId: string,
  onSuccess?: () => void
): UseProcedureArchiveReturn => {
  const queryClient = useQueryClient()

  const archiveMutation = useMutation(
    async ({
      procedureId,
      archive
    }: {
      procedureId: string
      archive: boolean
    }) => {
      const response = await postJSON({
        url: `/api/list/procedures/${procedureId}/archive`,
        method: 'PATCH',
        json: { archive }
      })

      return response as Procedure
    },
    {
      onSuccess: (_, variables) => {
        message.success(
          variables.archive
            ? 'Procedure archived successfully'
            : 'Procedure restored successfully'
        )
        // Invalidate procedures list to refetch
        queryClient.invalidateQueries(['procedures', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update procedure')
      }
    }
  )

  const archiveProcedure = async (procedureId: string): Promise<Procedure> => {
    return archiveMutation.mutateAsync({ procedureId, archive: true })
  }

  const unarchiveProcedure = async (
    procedureId: string
  ): Promise<Procedure> => {
    return archiveMutation.mutateAsync({ procedureId, archive: false })
  }

  return {
    archiveProcedure,
    unarchiveProcedure,
    isArchiving: archiveMutation.isLoading
  }
}
